/**
 * Google Maps URL utilities
 * Consolidated functions for generating Google Maps URLs
 */

export interface LocationData {
  location?: string | null;
  placeId?: string | null;
}

/**
 * Generate Google Maps search URL for a location
 * Uses place_id when available for more accurate results
 */
export function getGoogleMapsUrl(spot: LocationData): string {
  if (!spot.location) return "";
  const encodedLocation = encodeURIComponent(spot.location);
  if (spot.placeId) {
    return `https://www.google.com/maps/search/?api=1&query=${encodedLocation}&query_place_id=${spot.placeId}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`;
}

/**
 * Generate Google Maps directions URL for multiple locations
 * @returns URL string or null if less than 2 locations with valid addresses
 */
export function getGoogleMapsDirectionsUrl(spots: LocationData[]): string | null {
  const spotsWithLocation = spots.filter((s) => s.location);
  if (spotsWithLocation.length < 2) return null;

  const origin = spotsWithLocation[0].location!;
  const destination = spotsWithLocation[spotsWithLocation.length - 1].location!;
  const waypoints = spotsWithLocation.slice(1, -1).map((s) => s.location!);

  let url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}`;

  if (waypoints.length > 0) {
    url += `&waypoints=${waypoints.map(encodeURIComponent).join("|")}`;
  }

  return url;
}

/**
 * Open Google Maps in a new tab for a location
 */
export function openInGoogleMaps(spot: LocationData): void {
  const url = getGoogleMapsUrl(spot);
  if (url) {
    window.open(url, "_blank");
  }
}

/**
 * Open Google Maps directions in a new tab
 */
export function openGoogleMapsDirections(spots: LocationData[]): void {
  const url = getGoogleMapsDirectionsUrl(spots);
  if (url) {
    window.open(url, "_blank");
  }
}
